import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { HotelService } from '../services/hotelService';

/**
 * Owner signatures for a set of bookings, keyed by booking _id.
 *
 * Signatures are base64 images kept out of the list response. They are fetched
 * only once the element behind `ref` is scrolled into view, and only for
 * bookings the list reports as signed. A booking saved in this session still
 * carries its signature inline, and that copy wins.
 */
export function useLazyBookingSignatures(bookings, ref) {
    const { authorizedFetch } = useAuth();
    const [fetched, setFetched] = useState({});

    const needsFetch = bookings.filter(b =>
        Boolean(b.hasCheckInSignature || b.hasCheckOutSignature) &&
        !b.vetCheckIn?.ownerSignature && !b.vetCheckOut?.ownerSignature &&
        !fetched[b._id]
    );
    const pendingKey = needsFetch.map(b => b._id).join(',');

    useEffect(() => {
        const el = ref.current;
        if (!el || !pendingKey) return;

        const ids = pendingKey.split(',');
        let cancelled = false;
        const load = async () => {
            const results = await Promise.all(ids.map(id =>
                HotelService.getBookingSignatures(id, authorizedFetch).catch(err => {
                    // A missing signature should never break the card.
                    console.error('Error loading booking signatures:', err);
                    return { checkIn: null, checkOut: null };
                })
            ));
            if (cancelled) return;
            setFetched(prev => {
                const next = { ...prev };
                ids.forEach((id, i) => { next[id] = results[i]; });
                return next;
            });
        };

        // Without IntersectionObserver support, just load immediately.
        if (typeof IntersectionObserver === 'undefined') {
            load();
            return () => { cancelled = true; };
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                observer.disconnect();
                load();
            }
        }, { rootMargin: '200px' });

        observer.observe(el);
        return () => { cancelled = true; observer.disconnect(); };
    }, [pendingKey, ref, authorizedFetch]);

    return Object.fromEntries(bookings.map(b => [b._id, {
        checkIn:  b.vetCheckIn?.ownerSignature  || fetched[b._id]?.checkIn  || null,
        checkOut: b.vetCheckOut?.ownerSignature || fetched[b._id]?.checkOut || null,
    }]));
}

/**
 * The booking with its signatures put back. The vet-edit form replaces the
 * whole vetCheckIn subdocument, so the signature must travel with the booking
 * or it would be wiped on save.
 */
export function withSignatures(booking, sigs) {
    return {
        ...booking,
        ...(booking.vetCheckIn && {
            vetCheckIn: { ...booking.vetCheckIn, ownerSignature: sigs?.checkIn || undefined }
        }),
        ...(booking.vetCheckOut && {
            vetCheckOut: { ...booking.vetCheckOut, ownerSignature: sigs?.checkOut || undefined }
        }),
    };
}
